import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useMyDocumentsQuery, useMyDocumentsSummaryQuery } from "../../api/queries";
import { computeMyDocumentsCountSummary } from "../../lib/my-documents-count-summary";
import { DocumentsFilter } from "../../ui/DocumentsFilter";
import { DocumentsTable } from "../../ui/DocumentsTable";
import {
  MyDocumentsCountSummarySection,
  MyDocumentsCountSummarySectionSkeleton,
} from "../../ui/MyDocumentsCountSummarySection";
import { MyDocumentsSummaryCards } from "../../ui/MyDocumentsSummaryCards";

export function MyDocumentsPage() {
  const nav = useNavigate({ from: "/app/my-documents" });
  const search = useSearch({ from: "/app/my-documents" }) as OffsetPageQueryDTO & {
    documentType?: string;
  };

  const listQuery = useMyDocumentsQuery({
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort: search.sort,
    filters: search.filters,
  });

  const summaryQuery = useMyDocumentsSummaryQuery();
  const countSummary = computeMyDocumentsCountSummary(summaryQuery.data);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-bold text-2xl tracking-tight">
            ເອກະສານຂອງຂ້ອຍ
          </h1>
          <Button size="sm" onClick={() => nav({ to: "/app/my-documents/new" })}>
            ສ້າງໃໝ່
          </Button>
        </div>

        <div className="mb-6 space-y-4">
          {summaryQuery.isLoading ? (
            <MyDocumentsCountSummarySectionSkeleton />
          ) : (
            <MyDocumentsCountSummarySection summary={countSummary} />
          )}
          <MyDocumentsSummaryCards
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card p-4">
          <DocumentsFilter
            searchParams={search}
            fromRoute="/app/my-documents"
          />

          <div className="mt-4">
            <DocumentsTable
              data={listQuery.data?.data ?? []}
              isLoading={listQuery.isLoading}
              offset={search.offset ?? 0}
              limit={search.limit ?? 20}
              totalCount={listQuery.data?.meta?.total ?? 0}
              onPaginationChange={(offset, limit) =>
                nav({
                  search: {
                    offset,
                    limit,
                    sort: search.sort,
                    filters: search.filters,
                  },
                })
              }
              sortBy={search.sort?.[0]?.field}
              sortOrder={
                search.sort?.[0]?.dir as "asc" | "desc" | undefined
              }
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    offset: search.offset ?? 0,
                    limit: search.limit ?? 20,
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                    filters: search.filters,
                  },
                })
              }
              onView={(row) =>
                nav({
                  to: "/app/my-documents/$id",
                  params: { id: row.id },
                })
              }
              onEdit={(row) =>
                nav({
                  to: "/app/my-documents/$id/edit",
                  params: { id: row.id },
                })
              }
            />
          </div>
        </div>

        {!listQuery.isLoading &&
          (listQuery.data?.data?.length ?? 0) === 0 && (
            <p className="mt-6 text-center text-muted-foreground">
              ຍັງບໍ່ມີເອກະສານ — ກົດ &quot;ສ້າງໃໝ່&quot; ເພື່ອເລີ່ມ
            </p>
          )}
      </Main>
    </>
  );
}
